import React from 'react'
import { PageShell } from './PageShell'
import { BusinessDetailModal } from './BusinessDetailModal'
import { AdminAiHealthPage } from './AdminAiHealthPage'
import { AdminOverviewPage } from './AdminOverviewPage'
import { AdminQueuePage } from './AdminQueuePage'
import { Skeleton, SkeletonTableRows } from '../../../components/ui/Skeleton'
import { fetchAdminSchedulerSlots, patchAdminSchedulerSlot } from '../../../lib/nudgeApi'
import {
  fetchBillingMetrics,
  listAdminBusinesses,
  listAdminSubscriptions,
  adminCancelSubscription,
  adminChangePlan,
  adminAssignPlan,
  refundSubscription,
  createEntitlementOverride,
  searchAdminBusinesses,
  listAdminUsers,
} from '../../../lib/billingApi'
import { formatMoney } from '../../../lib/utils'
import type { SchedulerSlot } from '../../../types/api'
import { getPageTotal } from '../../../types/api'
import type {
  BillingCycle,
  BillingMetrics,
  AdminSubscriptionRow,
  AdminBusinessResult,
  AdminBusinessRow,
  AdminUserRow,
  PlanCode,
  SubscriptionStatus,
  FeatureKey,
} from '../../../types/api'

// ─── Constants ────────────────────────────────────────────────────────────────

const SUB_STATUS_COLORS: Record<SubscriptionStatus, string> = {
  INCOMPLETE: 'var(--text3)',
  TRIALING:   'var(--blue)',
  ACTIVE:     'var(--green)',
  PAST_DUE:   'var(--orange)',
  PAUSED:     'var(--text3)',
  GRACE:      'var(--orange)',
  CANCELED:   'var(--red)',
  EXPIRED:    'var(--text3)',
}

const ALL_SUB_STATUSES: SubscriptionStatus[] = [
  'INCOMPLETE', 'TRIALING', 'ACTIVE', 'PAST_DUE',
  'PAUSED', 'GRACE', 'CANCELED', 'EXPIRED',
]

const FEATURE_KEYS: FeatureKey[] = [
  'NUDGE_REPLY_AI', 'RECOVERY_ALERTS', 'CONTACT_LIMIT',
  'CUSTOM_SLOT_TIMES', 'OPT_OUT_ANALYTICS', 'PRIORITY_QUEUE', 'MAX_OWNER_USERS',
]

const PAGE_SIZE = 10

// ─── Shared UI helpers ────────────────────────────────────────────────────────

function SubStatusBadge({ status }: { status: SubscriptionStatus }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        fontWeight: 600,
        background: SUB_STATUS_COLORS[status] + '22',
        color: SUB_STATUS_COLORS[status],
        letterSpacing: '0.04em',
      }}
    >
      {status}
    </span>
  )
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 24,
          width: 440,
          maxWidth: '90vw',
        }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

// (Nudge Queue metrics moved into AdminQueuePage which embeds the full row table.)

// ─── Scheduler Slots tab ──────────────────────────────────────────────────────

type EditState = {
  fireTimeLocal: string
  active: boolean
}

function SchedulerSlotsTable({
  slots,
  onSave,
  saving,
}: {
  slots: SchedulerSlot[]
  onSave: (slot: SchedulerSlot, patch: { fireTimeLocal?: string; active?: boolean }) => void
  saving: string | null
}) {
  const [edits, setEdits] = React.useState<Record<string, EditState>>({})
  const [dirtyIds, setDirtyIds] = React.useState<Set<string>>(new Set())

  function getEdit(slot: SchedulerSlot): EditState {
    return edits[slot.slotId] ?? { fireTimeLocal: slot.fireTimeLocal, active: slot.active }
  }

  function updateEdit(slotId: string, partial: Partial<EditState>, origSlot: SchedulerSlot) {
    const current = edits[slotId] ?? { fireTimeLocal: origSlot.fireTimeLocal, active: origSlot.active }
    const next = { ...current, ...partial }
    setEdits(e => ({ ...e, [slotId]: next }))
    const isDirty = next.fireTimeLocal !== origSlot.fireTimeLocal || next.active !== origSlot.active
    setDirtyIds(ids => {
      const s = new Set(ids)
      if (isDirty) s.add(slotId); else s.delete(slotId)
      return s
    })
  }

  const slotOrder = { MORNING: 0, AFTERNOON: 1, EVENING: 2 }
  const sortedSlots = [...slots].sort((a, b) =>
    (slotOrder[a.slotCode] ?? 99) - (slotOrder[b.slotCode] ?? 99),
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {[['', sortedSlots] as const].map(([tz, tzSlots]) => (
        <div key={tz || 'global'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {tzSlots.map(slot => {
              const edit = getEdit(slot)
              const dirty = dirtyIds.has(slot.slotId)
              const isSaving = saving === slot.slotId
              return (
                <div
                  key={slot.slotId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr 90px 80px',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    background: 'var(--surface)',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--text2)' }}>
                    {slot.slotCode}
                  </div>
                  <input
                    type="time"
                    value={edit.fireTimeLocal}
                    onChange={e => updateEdit(slot.slotId, { fireTimeLocal: e.target.value }, slot)}
                    style={{
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '4px 8px',
                      fontSize: 13,
                      color: 'var(--text)',
                      fontFamily: "'DM Mono', monospace",
                      width: 110,
                    }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={edit.active}
                      onChange={e => updateEdit(slot.slotId, { active: e.target.checked }, slot)}
                      style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                    />
                    <span style={{ fontSize: 12, color: edit.active ? 'var(--green)' : 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                      {edit.active ? 'Active' : 'Off'}
                    </span>
                  </label>
                  <div>
                    {dirty && (
                      <button
                        className="btn-primary"
                        disabled={isSaving}
                        onClick={() => {
                          const patch: { fireTimeLocal?: string; active?: boolean } = {}
                          if (edit.fireTimeLocal !== slot.fireTimeLocal) patch.fireTimeLocal = edit.fireTimeLocal
                          if (edit.active !== slot.active) patch.active = edit.active
                          onSave(slot, patch)
                        }}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                      >
                        {isSaving ? '…' : 'Save'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Billing tab — Metrics summary ───────────────────────────────────────────

function BillingMetricsSection() {
  const [metrics, setMetrics] = React.useState<BillingMetrics | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  function load() {
    setLoading(true)
    setError(null)
    fetchBillingMetrics()
      .then(setMetrics)
      .catch(() => setError('Failed to load billing metrics.'))
      .finally(() => setLoading(false))
  }

  React.useEffect(() => { load() }, [])

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }} aria-hidden="true">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Skeleton height={40} width={40} borderRadius={8} />
          <div style={{ flex: 1 }}>
            <Skeleton height={12} width="40%" style={{ marginBottom: 6 }} />
            <Skeleton height={20} width="25%" />
          </div>
        </div>
      ))}
    </div>
  )
  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>
      <button className="btn-primary" onClick={load} style={{ width: 'fit-content', padding: '4px 12px', fontSize: 12 }}>Retry</button>
    </div>
  )
  if (!metrics) return null

  const active = metrics.countsByStatus['ACTIVE'] ?? 0
  const trialing = metrics.countsByStatus['TRIALING'] ?? 0
  const pastDue = metrics.countsByStatus['PAST_DUE'] ?? 0

  function MiniKpi({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: string; accent?: string }) {
    return (
      <div style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 600, color: accent ?? 'var(--text)', marginTop: 6, lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <MiniKpi label="MRR" value={formatMoney(metrics.mrrCents, metrics.currency)} sub="Recurring revenue" />
        <MiniKpi label="Active" value={active.toLocaleString()} sub="Paying subscribers" />
        <MiniKpi label="Trialing" value={trialing.toLocaleString()} sub="In free trial" />
        <MiniKpi label="Past due" value={pastDue.toLocaleString()} sub={pastDue > 0 ? 'Needs attention' : 'All settled'} accent={pastDue > 0 ? 'var(--orange)' : undefined} />
      </div>

      <div>
        <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Subscriptions by status
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {ALL_SUB_STATUSES.map(s => {
            const count = metrics.countsByStatus[s] ?? 0
            if (count === 0) return null
            return (
              <div
                key={s}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  background: 'var(--surface2)',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                }}
              >
                <SubStatusBadge status={s} />
                <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: 'var(--text)' }}>{count}</span>
              </div>
            )
          })}
          {ALL_SUB_STATUSES.every(s => (metrics.countsByStatus[s] ?? 0) === 0) && (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No subscriptions yet.</div>
          )}
        </div>
      </div>

      {metrics.topPlans.length > 0 && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Top plans
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {metrics.topPlans.map(p => (
              <div key={p.planCode} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--text2)', width: 110 }}>{p.planCode}</span>
                <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: 'var(--text)' }}>{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Billing tab — Subscriptions table ───────────────────────────────────────

function SubscriptionsTable() {
  const [rows, setRows] = React.useState<AdminSubscriptionRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [statusFilter, setStatusFilter] = React.useState<SubscriptionStatus | ''>('')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const [cancelTarget, setCancelTarget] = React.useState<AdminSubscriptionRow | null>(null)
  const [cancelReason, setCancelReason] = React.useState('')
  const [canceling, setCanceling] = React.useState(false)
  const [cancelError, setCancelError] = React.useState<string | null>(null)

  const [refundTarget, setRefundTarget] = React.useState<AdminSubscriptionRow | null>(null)
  const [refundAmountStr, setRefundAmountStr] = React.useState('')
  const [refundReason, setRefundReason] = React.useState('')
  const [refunding, setRefunding] = React.useState(false)
  const [refundError, setRefundError] = React.useState<string | null>(null)

  const [changePlanTarget, setChangePlanTarget] = React.useState<AdminSubscriptionRow | null>(null)
  const [newPlanCode, setNewPlanCode] = React.useState<PlanCode>('FREE')
  const [newBillingCycle, setNewBillingCycle] = React.useState<BillingCycle>('MONTHLY')
  const [changePlanCountInRevenue, setChangePlanCountInRevenue] = React.useState(true)
  const [changingPlan, setChangingPlan] = React.useState(false)
  const [changePlanError, setChangePlanError] = React.useState<string | null>(null)

  const load = React.useCallback((p: number, status: SubscriptionStatus | '') => {
    setLoading(true)
    setError(null)
    listAdminSubscriptions({ page: p, size: PAGE_SIZE, ...(status ? { status } : {}) })
      .then(res => { setRows(res.content ?? []); setTotal(getPageTotal(res)) })
      .catch(() => setError('Failed to load subscriptions.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => { load(page, statusFilter) }, [load, page, statusFilter])

  async function handleCancel() {
    if (!cancelTarget || !cancelReason.trim()) return
    setCanceling(true)
    setCancelError(null)
    try {
      await adminCancelSubscription(cancelTarget.id, cancelReason.trim())
      setCancelTarget(null)
      setCancelReason('')
      load(page, statusFilter)
    } catch {
      setCancelError('Failed to cancel subscription.')
    } finally {
      setCanceling(false)
    }
  }

  async function handleRefund() {
    if (!refundTarget || !refundAmountStr || !refundReason.trim()) return
    const amountCents = Math.round(parseFloat(refundAmountStr) * 100)
    if (isNaN(amountCents) || amountCents <= 0) { setRefundError('Enter a valid positive amount.'); return }
    setRefunding(true)
    setRefundError(null)
    try {
      await refundSubscription(refundTarget.id, amountCents, refundReason.trim())
      setRefundTarget(null)
      setRefundAmountStr('')
      setRefundReason('')
      load(page, statusFilter)
    } catch {
      setRefundError('Failed to process refund.')
    } finally {
      setRefunding(false)
    }
  }

  async function handleChangePlan() {
    if (!changePlanTarget) return
    setChangingPlan(true)
    setChangePlanError(null)
    try {
      await adminChangePlan(changePlanTarget.id, newPlanCode, newBillingCycle, changePlanCountInRevenue)
      setChangePlanTarget(null)
      load(page, statusFilter)
    } catch {
      setChangePlanError('Failed to change plan.')
    } finally {
      setChangingPlan(false)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <label style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>Status</label>
        <select
          value={statusFilter}
          onChange={e => { setPage(0); setStatusFilter(e.target.value as SubscriptionStatus | '') }}
          style={{
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 12,
            color: 'var(--text)',
            fontFamily: "'DM Mono', monospace",
          }}
        >
          <option value="">All</option>
          {ALL_SUB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {!loading && (
          <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
            {total} total
          </span>
        )}
      </div>

      {loading && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody><SkeletonTableRows cols={8} rows={4} /></tbody>
        </table>
      )}
      {error && <div style={{ fontSize: 13, color: 'var(--red)', padding: '16px 0' }}>{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text3)', padding: '16px 0' }}>No subscriptions found.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Business', 'Owner', 'Plan', 'Status', 'Cycle', 'Period end', 'MRR', 'Actions'].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        fontSize: 11,
                        color: 'var(--text3)',
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    style={{ borderBottom: '1px solid var(--border)', transition: 'background .12s ease' }}
                  >
                    <td style={{ padding: '8px 10px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap' }}>{row.businessName}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text2)', fontSize: 12 }}>{row.ownerEmail}</td>
                    <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{row.planDisplayName}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}><SubStatusBadge status={row.status} /></td>
                    <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text3)' }}>{row.billingCycle}</td>
                    <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                      {new Date(row.currentPeriodEnd).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                      {formatMoney(row.amountCents, row.currency)}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                        <button
                          className="btn-primary"
                          style={{ padding: '3px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          onClick={() => {
                            setChangePlanTarget(row)
                            setNewPlanCode((row.planCode as PlanCode) ?? 'FREE')
                            setNewBillingCycle((row.billingCycle as BillingCycle) ?? 'MONTHLY')
                            setChangePlanCountInRevenue(row.amountCents > 0)
                            setChangePlanError(null)
                          }}
                        >
                          Change plan
                        </button>
                        {row.status !== 'CANCELED' && row.status !== 'EXPIRED' && (
                          <button
                            className="btn-danger"
                            style={{ padding: '3px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                            onClick={() => { setCancelTarget(row); setCancelReason(''); setCancelError(null) }}
                          >
                            Force cancel
                          </button>
                        )}
                        <button
                          className="btn-primary"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => { setRefundTarget(row); setRefundAmountStr(''); setRefundReason(''); setRefundError(null) }}
                        >
                          Refund
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn-primary"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                ← Prev
              </button>
              <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                {page + 1} / {totalPages}
              </span>
              <button
                className="btn-primary"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {cancelTarget && (
        <ModalOverlay onClose={() => setCancelTarget(null)}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Force Cancel Subscription</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            {cancelTarget.businessName} · {cancelTarget.planDisplayName}
          </div>
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Reason *</label>
          <input
            type="text"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            placeholder="e.g. fraud, non-payment, owner request"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 10px',
              fontSize: 13,
              color: 'var(--text)',
              marginBottom: 12,
            }}
          />
          {cancelError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{cancelError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={() => setCancelTarget(null)} style={{ padding: '6px 14px', fontSize: 13 }}>
              Back
            </button>
            <button
              className="btn-danger"
              disabled={!cancelReason.trim() || canceling}
              onClick={handleCancel}
              style={{ padding: '6px 14px', fontSize: 13 }}
            >
              {canceling ? 'Canceling…' : 'Confirm cancel'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {changePlanTarget && (
        <ModalOverlay onClose={() => setChangePlanTarget(null)}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Change Plan</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
            {changePlanTarget.businessName} · currently <strong>{changePlanTarget.planCode}</strong> / {changePlanTarget.billingCycle}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>New plan</label>
              <select
                value={newPlanCode}
                onChange={e => setNewPlanCode(e.target.value as PlanCode)}
                style={{
                  width: '100%',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 13,
                  color: 'var(--text)',
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                {(['FREE', 'PRO', 'PRO_PLUS', 'ENTERPRISE'] as PlanCode[]).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Billing cycle</label>
              <select
                value={newBillingCycle}
                onChange={e => setNewBillingCycle(e.target.value as BillingCycle)}
                style={{
                  width: '100%',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 13,
                  color: 'var(--text)',
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                <option value="MONTHLY">Monthly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
          </div>
          <RevenueToggle value={changePlanCountInRevenue} onChange={setChangePlanCountInRevenue} planCode={newPlanCode} />
          {changePlanError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{changePlanError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={() => setChangePlanTarget(null)} style={{ padding: '6px 14px', fontSize: 13 }}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={changingPlan}
              onClick={handleChangePlan}
              style={{ padding: '6px 14px', fontSize: 13 }}
            >
              {changingPlan ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {refundTarget && (
        <ModalOverlay onClose={() => setRefundTarget(null)}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Process Refund</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            {refundTarget.businessName} · last charge {formatMoney(refundTarget.amountCents, refundTarget.currency)}
          </div>
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>
            Amount ({refundTarget.currency})
          </label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={refundAmountStr}
            onChange={e => setRefundAmountStr(e.target.value)}
            placeholder="0.00"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 10px',
              fontSize: 13,
              color: 'var(--text)',
              fontFamily: "'DM Mono', monospace",
              marginBottom: 10,
            }}
          />
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Reason *</label>
          <input
            type="text"
            value={refundReason}
            onChange={e => setRefundReason(e.target.value)}
            placeholder="e.g. service outage, owner request"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 10px',
              fontSize: 13,
              color: 'var(--text)',
              marginBottom: 12,
            }}
          />
          {refundError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{refundError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={() => setRefundTarget(null)} style={{ padding: '6px 14px', fontSize: 13 }}>
              Back
            </button>
            <button
              className="btn-danger"
              disabled={!refundAmountStr || !refundReason.trim() || refunding}
              onClick={handleRefund}
              style={{ padding: '6px 14px', fontSize: 13 }}
            >
              {refunding ? 'Processing…' : 'Issue refund'}
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}

// ─── Billing tab — Entitlement overrides form ─────────────────────────────────

const FEATURE_KEY_LABELS: Record<FeatureKey, { label: string; description: string; type: 'bool' | 'int' }> = {
  NUDGE_REPLY_AI:      { label: 'AI Reply',            description: 'AI-generated nudge reply messages',    type: 'bool' },
  RECOVERY_ALERTS:     { label: 'Recovery Alerts',     description: 'Real-time recovery alert notifications', type: 'bool' },
  CONTACT_LIMIT:       { label: 'Contact Limit',       description: 'Max contacts allowed (−1 = unlimited)', type: 'int'  },
  CUSTOM_SLOT_TIMES:   { label: 'Custom Slot Times',   description: 'Custom scheduler fire times',           type: 'bool' },
  OPT_OUT_ANALYTICS:   { label: 'Opt-Out Analytics',   description: 'Access to opt-out analytics reports',   type: 'bool' },
  PRIORITY_QUEUE:      { label: 'Priority Queue',      description: 'Priority nudge queue processing',       type: 'bool' },
  MAX_OWNER_USERS:     { label: 'Max Owner Users',     description: 'Max owner-role users per business',     type: 'int'  },
}

function BusinessSearchCombobox({
  value,
  onChange,
}: {
  value: AdminBusinessResult | null
  onChange: (b: AdminBusinessResult | null) => void
}) {
  const [query, setQuery] = React.useState('')
  const [allBusinesses, setAllBusinesses] = React.useState<AdminBusinessResult[]>([])
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const filtered = query.trim()
    ? allBusinesses.filter(b => {
        const q = query.toLowerCase()
        return b.businessName.toLowerCase().includes(q) || b.ownerEmail.toLowerCase().includes(q)
      })
    : allBusinesses

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleFocus() {
    setOpen(true)
    if (loaded) return
    setLoading(true)
    try {
      const res = await searchAdminBusinesses('')
      setAllBusinesses(res)
      setLoaded(true)
    } catch {
      // silently fail — user can still type to retry on next focus
    } finally {
      setLoading(false)
    }
  }

  if (value) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 12px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value.businessName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{value.ownerEmail}</div>
        </div>
        <button
          type="button"
          onClick={() => { onChange(null); setQuery(''); setLoaded(false); setAllBusinesses([]) }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text3)',
            fontSize: 16,
            lineHeight: 1,
            padding: '2px 4px',
            borderRadius: 4,
            flexShrink: 0,
          }}
          title="Clear selection"
        >
          ✕
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={handleFocus}
          placeholder="Search by business name or email…"
          autoComplete="off"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'var(--surface2)',
            border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: open ? '8px 8px 0 0' : 8,
            padding: '9px 36px 9px 12px',
            fontSize: 13,
            color: 'var(--text)',
            outline: 'none',
          }}
        />
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }}>
          {loading
            ? <span style={{ fontSize: 12 }}>…</span>
            : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            )
          }
        </div>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1px solid var(--accent)',
            borderTop: '1px solid var(--border)',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            zIndex: 50,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text3)' }}>
              {query.trim() ? 'No businesses match.' : 'No businesses found.'}
            </div>
          )}
          {!loading && filtered.map(r => (
            <div
              key={r.businessId}
              onMouseDown={() => { onChange(r); setQuery(''); setOpen(false) }}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{r.businessName}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{r.ownerEmail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EntitlementOverrideForm() {
  const [selectedBusiness, setSelectedBusiness] = React.useState<AdminBusinessResult | null>(null)
  const [featureKey, setFeatureKey] = React.useState<FeatureKey>('NUDGE_REPLY_AI')
  const [boolValue, setBoolValue] = React.useState(true)
  const [intValue, setIntValue] = React.useState('')
  const [expiresAt, setExpiresAt] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)

  const featureMeta = FEATURE_KEY_LABELS[featureKey]

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedBusiness) { setError('Select a business first.'); return }
    if (featureMeta.type === 'int' && (intValue === '' || isNaN(parseInt(intValue, 10)))) {
      setError('Enter a valid integer value.')
      return
    }
    setSubmitting(true)
    setError(null)
    setSuccess(false)
    try {
      await createEntitlementOverride({
        businessId: selectedBusiness.businessId,
        featureKey,
        ...(featureMeta.type === 'bool' ? { boolValue } : { intValue: parseInt(intValue, 10) }),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
      setSuccess(true)
      setSelectedBusiness(null)
      setReason('')
      setExpiresAt('')
      setIntValue('')
    } catch {
      setError('Failed to create override.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Step 1 — Business */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          1. Select business
        </label>
        <BusinessSearchCombobox value={selectedBusiness} onChange={setSelectedBusiness} />
      </div>

      {/* Step 2 — Feature */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          2. Choose feature
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {FEATURE_KEYS.map(k => {
            const meta = FEATURE_KEY_LABELS[k]
            const selected = featureKey === k
            return (
              <label
                key={k}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                  background: selected ? 'var(--accent)18' : 'var(--surface2)',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="radio"
                  name="featureKey"
                  value={k}
                  checked={selected}
                  onChange={() => { setFeatureKey(k); setIntValue('') }}
                  style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{meta.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{meta.description}</div>
                </div>
                <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', flexShrink: 0 }}>{k}</span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Step 3 — Value */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          3. Set value
        </label>
        {featureMeta.type === 'bool' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {([true, false] as const).map(v => (
              <label
                key={String(v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: `1px solid ${boolValue === v ? (v ? 'var(--green)' : 'var(--red)') : 'var(--border)'}`,
                  background: boolValue === v ? (v ? 'var(--green)' : 'var(--red)') + '18' : 'var(--surface2)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: 13,
                  fontWeight: 500,
                  color: boolValue === v ? (v ? 'var(--green)' : 'var(--red)') : 'var(--text3)',
                }}
              >
                <input type="radio" name="boolValue" checked={boolValue === v} onChange={() => setBoolValue(v)} style={{ accentColor: v ? 'var(--green)' : 'var(--red)' }} />
                {v ? 'Enabled' : 'Disabled'}
              </label>
            ))}
          </div>
        ) : (
          <div>
            <input
              type="number"
              value={intValue}
              onChange={e => setIntValue(e.target.value)}
              placeholder="e.g. 500 or −1 for unlimited"
              style={{
                width: 220,
                boxSizing: 'border-box',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '9px 12px',
                fontSize: 13,
                color: 'var(--text)',
                fontFamily: "'DM Mono', monospace",
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Use −1 for unlimited</div>
          </div>
        )}
      </div>

      {/* Optional fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Expires at (optional)</label>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              color: 'var(--text)',
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. outage comp, trial extension"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              color: 'var(--text)',
            }}
          />
        </div>
      </div>

      {error && <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>}
      {success && <div style={{ fontSize: 13, color: 'var(--green)' }}>Override created successfully.</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || !selectedBusiness}
          style={{ padding: '9px 20px', fontSize: 13 }}
        >
          {submitting ? 'Creating…' : 'Create override'}
        </button>
      </div>
    </form>
  )
}

// ─── Shared revenue toggle ────────────────────────────────────────────────────

function RevenueToggle({ value, onChange, planCode }: { value: boolean; onChange: (v: boolean) => void; planCode: PlanCode }) {
  const isFree = planCode === 'FREE'
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 8,
      background: value && !isFree ? 'var(--green)0d' : 'var(--surface2)',
      border: `1px solid ${value && !isFree ? 'var(--green)44' : 'var(--border)'}`,
      marginBottom: 14,
    }}>
      <input
        type="checkbox"
        id="countInRevenue"
        checked={isFree ? false : value}
        disabled={isFree}
        onChange={e => onChange(e.target.checked)}
        style={{ accentColor: 'var(--green)', marginTop: 2, flexShrink: 0, width: 14, height: 14 }}
      />
      <label htmlFor="countInRevenue" style={{ cursor: isFree ? 'default' : 'pointer', flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: isFree ? 'var(--text3)' : 'var(--text)' }}>
          Count toward MRR
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
          {isFree
            ? 'Free plan has no revenue impact'
            : value
              ? 'This subscription will be included in revenue calculations'
              : 'Exclude from MRR — use for comped, internal, or partner accounts'}
        </div>
      </label>
    </div>
  )
}

// ─── Billing tab — Assign / upgrade plan ──────────────────────────────────────

// Current marketing catalog only. Legacy codes (STARTER/GROWTH/BUSINESS, PRO v1)
// still resolve in EntitlementService for already-active subscriptions, but
// new admin assignments should target the current catalog.
const PLAN_CODES: { code: PlanCode; label: string; description: string }[] = [
  { code: 'FREE',       label: 'Free',       description: 'No charge, basic access'        },
  { code: 'PRO',        label: 'Pro',        description: '1,000 contacts, $29/mo'         },
  { code: 'PRO_PLUS',   label: 'Pro+',       description: 'Metered: $29 per 1,000 block'   },
  { code: 'ENTERPRISE', label: 'Enterprise', description: 'Custom limits, franchise'       },
]

function AssignPlanForm() {
  const [selectedBusiness, setSelectedBusiness] = React.useState<AdminBusinessResult | null>(null)
  const [planCode, setPlanCode] = React.useState<PlanCode>('PRO')
  const [billingCycle, setBillingCycle] = React.useState<BillingCycle>('MONTHLY')
  const [countInRevenue, setCountInRevenue] = React.useState(true)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedBusiness) { setError('Select a business first.'); return }
    setSubmitting(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await adminAssignPlan(selectedBusiness.businessId, planCode, billingCycle, countInRevenue)
      setSuccess(`${selectedBusiness.businessName} upgraded to ${result.planDisplayName} (${billingCycle.toLowerCase()}).`)
      setSelectedBusiness(null)
      setPlanCode('PRO')
      setBillingCycle('MONTHLY')
    } catch {
      setError('Failed to assign plan. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Step 1 — Business */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          1. Select business
        </label>
        <BusinessSearchCombobox value={selectedBusiness} onChange={b => { setSelectedBusiness(b); setError(null); setSuccess(null) }} />
      </div>

      {/* Step 2 — Plan */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          2. Choose plan
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {PLAN_CODES.map(p => {
            const selected = planCode === p.code
            return (
              <label
                key={p.code}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                  background: selected ? 'var(--accent)18' : 'var(--surface2)',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="planCode"
                    value={p.code}
                    checked={selected}
                    onChange={() => setPlanCode(p.code)}
                    style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: selected ? 'var(--accent)' : 'var(--text)' }}>
                    {p.label}
                  </span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: 22 }}>{p.description}</span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Step 3 — Billing cycle */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
          3. Billing cycle
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['MONTHLY', 'ANNUAL'] as BillingCycle[]).map(c => (
            <label
              key={c}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '9px 18px',
                borderRadius: 8,
                border: `1px solid ${billingCycle === c ? 'var(--accent)' : 'var(--border)'}`,
                background: billingCycle === c ? 'var(--accent)18' : 'var(--surface2)',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: 13,
                fontWeight: billingCycle === c ? 600 : 400,
                color: billingCycle === c ? 'var(--accent)' : 'var(--text3)',
              }}
            >
              <input
                type="radio"
                name="billingCycle"
                value={c}
                checked={billingCycle === c}
                onChange={() => setBillingCycle(c)}
                style={{ accentColor: 'var(--accent)' }}
              />
              {c === 'MONTHLY' ? 'Monthly' : 'Annual'}
            </label>
          ))}
        </div>
      </div>

      {/* Summary */}
      {selectedBusiness && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 8,
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          fontSize: 13,
          color: 'var(--text2)',
        }}>
          Assigning <strong style={{ color: 'var(--text)' }}>{PLAN_CODES.find(p => p.code === planCode)?.label}</strong> ({billingCycle.toLowerCase()}) to{' '}
          <strong style={{ color: 'var(--text)' }}>{selectedBusiness.businessName}</strong> ({selectedBusiness.ownerEmail})
        </div>
      )}

      <RevenueToggle value={countInRevenue} onChange={setCountInRevenue} planCode={planCode} />

      {error && <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>}
      {success && <div style={{ fontSize: 13, color: 'var(--green)' }}>{success}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || !selectedBusiness}
          style={{ padding: '9px 20px', fontSize: 13 }}
        >
          {submitting ? 'Assigning…' : 'Assign plan'}
        </button>
      </div>
    </form>
  )
}

// ─── Billing inner tabs ───────────────────────────────────────────────────────

type BillingInnerTab = 'metrics' | 'subscriptions' | 'assign-plan' | 'overrides'

const BILLING_INNER_TABS: { id: BillingInnerTab; label: string; sub: string }[] = [
  { id: 'metrics',      label: 'Billing Metrics',      sub: 'MRR and subscription counts across all businesses'     },
  { id: 'subscriptions',label: 'Subscriptions',         sub: 'Filter, inspect, cancel, or refund any subscription'  },
  { id: 'assign-plan',  label: 'Assign Plan',           sub: 'Upgrade or assign a subscription plan to any business' },
  { id: 'overrides',    label: 'Entitlement Overrides', sub: 'Grant or restrict a feature for a specific business'   },
]

function BillingTabContent() {
  const [innerTab, setInnerTab] = React.useState<BillingInnerTab>('metrics')
  const active = BILLING_INNER_TABS.find(t => t.id === innerTab)!

  return (
    <div className="panel">
      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 2,
          padding: '16px 20px 0',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {BILLING_INNER_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setInnerTab(t.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: innerTab === t.id ? 600 : 400,
              color: innerTab === t.id ? 'var(--text)' : 'var(--text3)',
              borderBottom: innerTab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
              transition: 'color .15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="panel-header">
        <div>
          <div className="panel-title">{active.label}</div>
          <div className="panel-sub">{active.sub}</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '12px 20px 20px' }}>
        {innerTab === 'metrics'       && <BillingMetricsSection />}
        {innerTab === 'subscriptions' && <SubscriptionsTable />}
        {innerTab === 'assign-plan'   && <AssignPlanForm />}
        {innerTab === 'overrides'     && <EntitlementOverrideForm />}
      </div>
    </div>
  )
}

// ─── Admin — Businesses page ──────────────────────────────────────────────────

const BIZ_PAGE_SIZE = 15

function AdminBusinessesPage({ isActive }: { isActive: boolean }) {
  const [rows, setRows] = React.useState<AdminBusinessRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [planFilter, setPlanFilter] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const [assignTarget, setAssignTarget] = React.useState<AdminBusinessRow | null>(null)
  const [assignPlan, setAssignPlan] = React.useState<PlanCode>('PRO')
  const [assignCycle, setAssignCycle] = React.useState<BillingCycle>('MONTHLY')
  const [assignCountInRevenue, setAssignCountInRevenue] = React.useState(true)
  const [assigning, setAssigning] = React.useState(false)
  const [assignError, setAssignError] = React.useState<string | null>(null)

  const [detailId, setDetailId] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [search])

  const load = React.useCallback((p: number, plan: string, status: string, q: string) => {
    setLoading(true)
    setError(null)
    listAdminBusinesses({
      page: p,
      size: BIZ_PAGE_SIZE,
      ...(plan   ? { planCode: plan }   : {}),
      ...(status ? { status }           : {}),
      ...(q      ? { q }                : {}),
    })
      .then(res => { setRows(res.content ?? []); setTotal(getPageTotal(res)) })
      .catch(() => setError('Failed to load businesses.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => {
    if (isActive) load(page, planFilter, statusFilter, debouncedSearch)
  }, [isActive, load, page, planFilter, statusFilter, debouncedSearch])

  React.useEffect(() => { setPage(0) }, [debouncedSearch])

  async function handleAssign() {
    if (!assignTarget) return
    setAssigning(true)
    setAssignError(null)
    try {
      await adminAssignPlan(assignTarget.businessId, assignPlan, assignCycle, assignCountInRevenue)
      setAssignTarget(null)
      load(page, planFilter, statusFilter, debouncedSearch)
    } catch {
      setAssignError('Failed to assign plan.')
    } finally {
      setAssigning(false)
    }
  }

  const totalPages = Math.ceil(total / BIZ_PAGE_SIZE)

  const inputStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 12,
    color: 'var(--text)',
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Businesses</div>
          <div className="panel-sub">All registered businesses and their current plan</div>
        </div>
      </div>
      <div style={{ padding: '12px 20px 20px' }}>

        {/* Search + filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 360 }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by business or email…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 30px 6px 30px',
                fontSize: 13,
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <svg
              viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 14, height: 14, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '2px 6px' }}
                aria-label="Clear"
              >×</button>
            )}
          </div>
          <select value={planFilter} onChange={e => { setPage(0); setPlanFilter(e.target.value) }} style={inputStyle}>
            <option value="">All plans</option>
            {(['FREE', 'STARTER', 'GROWTH', 'PRO', 'PRO_PLUS', 'BUSINESS', 'ENTERPRISE'] as PlanCode[]).map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => { setPage(0); setStatusFilter(e.target.value) }} style={inputStyle}>
            <option value="">All statuses</option>
            {ALL_SUB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {loading ? 'Loading…' : `${(total ?? 0).toLocaleString()} ${total === 1 ? 'business' : 'businesses'}`}
          </span>
        </div>

        {/* Table */}
        {loading && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody><SkeletonTableRows cols={7} rows={5} /></tbody>
          </table>
        )}
        {error && <div style={{ fontSize: 13, color: 'var(--red)', padding: '16px 0' }}>{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--text3)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
              {debouncedSearch || planFilter || statusFilter
                ? 'No businesses match these filters'
                : 'No businesses yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {debouncedSearch || planFilter || statusFilter
                ? 'Try clearing your search or filters.'
                : 'Businesses will appear here once owners complete onboarding.'}
            </div>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Business', 'Owner', 'Type', 'Plan', 'Billing', 'Contacts', 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        fontSize: 11,
                        color: 'var(--text3)',
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.businessId}
                      onClick={() => setDetailId(row.businessId)}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .12s ease' }}
                    >
                      <td style={{ padding: '9px 10px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {row.businessName}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{row.ownerName ?? '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{row.ownerEmail}</div>
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {row.businessType ?? '—'}
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                        {row.planDisplayName}
                      </td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                        {row.subscriptionStatus
                          ? <SubStatusBadge status={row.subscriptionStatus} />
                          : <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--text)', textAlign: 'right' }}>
                        {row.contactCount}
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' }}>
                        {new Date(row.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <button
                          className="btn-primary"
                          style={{ padding: '3px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          onClick={e => {
                            e.stopPropagation()
                            setAssignTarget(row)
                            setAssignPlan((row.planCode as PlanCode) ?? 'PRO')
                            setAssignCycle('MONTHLY')
                            setAssignCountInRevenue(true)
                            setAssignError(null)
                          }}
                        >
                          Assign plan
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                <button className="btn-primary" disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '4px 10px', fontSize: 12 }}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>{page + 1} / {totalPages}</span>
                <button className="btn-primary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '4px 10px', fontSize: 12 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Business detail modal */}
      {detailId && (
        <BusinessDetailModal businessId={detailId} onClose={() => setDetailId(null)} />
      )}

      {/* Assign plan modal */}
      {assignTarget && (
        <ModalOverlay onClose={() => setAssignTarget(null)}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Assign Plan</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
            {assignTarget.businessName} · {assignTarget.ownerEmail}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Plan</label>
              <select
                value={assignPlan}
                onChange={e => setAssignPlan(e.target.value as PlanCode)}
                style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: 'var(--text)', fontFamily: "'DM Mono', monospace" }}
              >
                {(['FREE', 'PRO', 'PRO_PLUS', 'ENTERPRISE'] as PlanCode[]).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Billing cycle</label>
              <select
                value={assignCycle}
                onChange={e => setAssignCycle(e.target.value as BillingCycle)}
                style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: 'var(--text)', fontFamily: "'DM Mono', monospace" }}
              >
                <option value="MONTHLY">Monthly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
          </div>
          <RevenueToggle value={assignCountInRevenue} onChange={setAssignCountInRevenue} planCode={assignPlan} />
          {assignError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{assignError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={() => setAssignTarget(null)} style={{ padding: '6px 14px', fontSize: 13 }}>Cancel</button>
            <button className="btn-primary" disabled={assigning} onClick={handleAssign} style={{ padding: '6px 14px', fontSize: 13 }}>
              {assigning ? 'Saving…' : 'Assign'}
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}

// ─── Admin — Users page ───────────────────────────────────────────────────────

const USER_PAGE_SIZE = 15

type ProfileFilter = '' | 'true' | 'false'

function AdminUsersPage({ isActive }: { isActive: boolean }) {
  const [rows, setRows] = React.useState<AdminUserRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [profileFilter, setProfileFilter] = React.useState<ProfileFilter>('')
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [search])

  const load = React.useCallback((p: number, filter: ProfileFilter, q: string) => {
    setLoading(true)
    setError(null)
    listAdminUsers({
      page: p,
      size: USER_PAGE_SIZE,
      ...(filter !== '' ? { hasBusinessProfile: filter === 'true' } : {}),
      ...(q ? { q } : {}),
    })
      .then(res => { setRows(res.content ?? []); setTotal(getPageTotal(res)) })
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => {
    if (isActive) load(page, profileFilter, debouncedSearch)
  }, [isActive, load, page, profileFilter, debouncedSearch])

  React.useEffect(() => { setPage(0) }, [debouncedSearch, profileFilter])

  const totalPages = Math.ceil(total / USER_PAGE_SIZE)

  const inputStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 12,
    color: 'var(--text)',
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Users</div>
          <div className="panel-sub">All registered user accounts</div>
        </div>
      </div>
      <div style={{ padding: '12px 20px 20px' }}>

        {/* Search + filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 360 }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by email or name…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 30px 6px 30px',
                fontSize: 13,
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <svg
              viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 14, height: 14, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '2px 6px' }}
                aria-label="Clear"
              >×</button>
            )}
          </div>
          <select
            value={profileFilter}
            onChange={e => { setPage(0); setProfileFilter(e.target.value as ProfileFilter) }}
            style={inputStyle}
          >
            <option value="">All users</option>
            <option value="true">With business profile</option>
            <option value="false">Without business profile</option>
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {loading ? 'Loading…' : `${(total ?? 0).toLocaleString()} ${total === 1 ? 'user' : 'users'}`}
          </span>
        </div>

        {/* Table */}
        {loading && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody><SkeletonTableRows cols={6} rows={5} /></tbody>
          </table>
        )}
        {error && <div style={{ fontSize: 13, color: 'var(--red)', padding: '16px 0' }}>{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
              {debouncedSearch || profileFilter ? 'No users match these filters' : 'No users yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {debouncedSearch || profileFilter ? 'Try clearing your search or filter.' : 'Users will appear here once they sign up.'}
            </div>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Email', 'Name', 'Role', 'Status', 'Provider', 'Business', 'Joined'].map(h => (
                      <th key={h} style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        fontSize: 11,
                        color: 'var(--text3)',
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.userId}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      style={{ borderBottom: '1px solid var(--border)', transition: 'background .12s ease' }}
                    >
                      <td style={{ padding: '9px 10px', color: 'var(--text)', whiteSpace: 'nowrap' }}>{row.email}</td>
                      <td style={{ padding: '9px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                        {row.firstName || row.lastName
                          ? `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim()
                          : <span style={{ color: 'var(--text3)' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                        {row.role}
                      </td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontFamily: "'DM Mono', monospace",
                          fontWeight: 600,
                          background: row.isActive ? 'var(--green)22' : 'var(--text3)22',
                          color: row.isActive ? 'var(--green)' : 'var(--text3)',
                        }}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {row.authProvider}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        {row.hasBusinessProfile && row.businessName
                          ? <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{row.businessName}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' }}>
                        {new Date(row.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                <button className="btn-primary" disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '4px 10px', fontSize: 12 }}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>{page + 1} / {totalPages}</span>
                <button className="btn-primary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '4px 10px', fontSize: 12 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Admin page ───────────────────────────────────────────────────────────────

export type AdminTab = 'overview' | 'metrics' | 'slots' | 'billing' | 'businesses' | 'ai-health' | 'users'

export function AdminPage({ isActive, activeTab }: { isActive: boolean; activeTab: AdminTab }) {
  const adminTab = activeTab

  const [slots, setSlots] = React.useState<SchedulerSlot[]>([])
  const [slotsLoading, setSlotsLoading] = React.useState(false)
  const [slotsError, setSlotsError] = React.useState<string | null>(null)
  const [savingSlot, setSavingSlot] = React.useState<string | null>(null)
  const [saveError, setSaveError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!isActive) return
    let cancelled = false

    async function load() {
      setSlotsLoading(true)
      setSlotsError(null)
      try {
        const result = await fetchAdminSchedulerSlots()
        if (!cancelled) setSlots(result)
      } catch {
        if (!cancelled) setSlotsError('Failed to load scheduler slots.')
      } finally {
        if (!cancelled) setSlotsLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [isActive])

  async function handleSaveSlot(slot: SchedulerSlot, patch: { fireTimeLocal?: string; active?: boolean }) {
    setSavingSlot(slot.slotId)
    setSaveError(null)
    try {
      const updated = await patchAdminSchedulerSlot(slot.slotId, patch)
      setSlots(prev => prev.map(s => (s.slotId === updated.slotId ? updated : s)))
    } catch {
      setSaveError('Failed to save slot. Please try again.')
    } finally {
      setSavingSlot(null)
    }
  }

  return (
    <PageShell id="page-admin" isActive={isActive}>
      <div className="content">

        {/* Nudge Queue tab */}
        {adminTab === 'metrics' && <AdminQueuePage />}

        {/* Scheduler Slots tab */}
        {adminTab === 'slots' && (
          <div className="panel">
            <div className="panel-header">
              <div>
                <div className="panel-title">Scheduler Slots</div>
                <div className="panel-sub">Configure fire times and active state per timezone</div>
              </div>
            </div>
            <div style={{ padding: '12px 20px 20px' }}>
              {slotsLoading && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-hidden="true">
                  <tbody><SkeletonTableRows cols={5} rows={5} /></tbody>
                </table>
              )}
              {slotsError && <div style={{ fontSize: 13, color: 'var(--red)', padding: '20px 0' }}>{slotsError}</div>}
              {saveError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 12 }}>{saveError}</div>}
              {!slotsLoading && !slotsError && slots.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '20px 0' }}>No scheduler slots found.</div>
              )}
              {!slotsLoading && !slotsError && slots.length > 0 && (
                <SchedulerSlotsTable slots={slots} onSave={handleSaveSlot} saving={savingSlot} />
              )}
            </div>
          </div>
        )}

        {/* Billing tab */}
        {adminTab === 'billing' && <BillingTabContent />}

        {/* Businesses tab */}
        {adminTab === 'businesses' && <AdminBusinessesPage isActive={adminTab === 'businesses'} />}

        {/* Users tab */}
        {adminTab === 'users' && <AdminUsersPage isActive={adminTab === 'users'} />}

        {/* AI Health tab */}
        {adminTab === 'ai-health' && <AdminAiHealthPage />}

        {/* Overview tab */}
        {adminTab === 'overview' && <AdminOverviewPage />}
      </div>
    </PageShell>
  )
}
